import { z } from "zod";
import {
  baseToolPatchPayloadSchema,
  baseToolPostPayloadSchema
} from "./base.js";
const CustomToolType = "custom";
const defaultCustomToolTimeoutMs = 3e4;
const CustomToolHttpMethod = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH"
};
const customToolHttpMethodSchema = z.enum([
  CustomToolHttpMethod.GET,
  CustomToolHttpMethod.POST,
  CustomToolHttpMethod.PUT,
  CustomToolHttpMethod.PATCH
]);
const CustomToolParameterType = {
  STRING: "string",
  NUMBER: "number",
  BOOLEAN: "boolean",
  OBJECT: "object",
  ARRAY: "array"
};
const customToolParameterTypeSchema = z.enum([
  CustomToolParameterType.STRING,
  CustomToolParameterType.NUMBER,
  CustomToolParameterType.BOOLEAN,
  CustomToolParameterType.OBJECT,
  CustomToolParameterType.ARRAY
]);
const customToolParameterArrayItemTypeSchema = z.enum([
  CustomToolParameterType.STRING,
  CustomToolParameterType.NUMBER,
  CustomToolParameterType.BOOLEAN,
  CustomToolParameterType.OBJECT
]);
const customToolParameterSchema = z.object({
  name: z.string().nonempty(),
  description: z.string().optional(),
  type: customToolParameterTypeSchema,
  array_item_type: customToolParameterArrayItemTypeSchema.optional(),
  required: z.boolean().optional()
});
const pathPlaceholderNamePattern = /^[A-Za-z_][A-Za-z0-9_]*$/;
const customToolPathParameterSchema = z.object({
  name: z.string().regex(pathPlaceholderNamePattern),
  /** AI guidance for what the agent should put in this path segment. */
  description: z.string().nonempty()
});
function extractCustomToolPathPlaceholders(url) {
  const names = [];
  for (const segment of getCustomToolUrlPathSegments(url)) {
    if (!segment.startsWith(":")) {
      continue;
    }
    const name = segment.slice(1);
    if (pathPlaceholderNamePattern.test(name) && !names.includes(name)) {
      names.push(name);
    }
  }
  return names;
}
function getCustomToolUrlPathSegments(url) {
  try {
    return new URL(url).pathname.split("/");
  } catch {
    return [];
  }
}
const CustomToolMockResponseType = {
  STRING: "string",
  JSON: "json"
};
const customToolMockResponseTypeSchema = z.enum([
  CustomToolMockResponseType.STRING,
  CustomToolMockResponseType.JSON
]);
const customToolMockSchema = z.object({
  /**
   * The mock response content to return when this tool is called within a simulation.
   */
  response: z.string().nonempty(),
  /**
   * Type of the mock response.
   * - "string": Plain text response
   * - "json": JSON object response (will be validated as JSON)
   */
  type: customToolMockResponseTypeSchema
});
const customToolSettingsSchema = z.object({
  method: customToolHttpMethodSchema,
  url: z.string().nonempty(),
  timeout_ms: z.number().positive().optional(),
  headers: z.record(z.string(), z.string()).optional(),
  query_params: z.record(z.string(), z.string()).optional(),
  parameters: z.array(customToolParameterSchema).optional(),
  /**
   * AI guidance for the path placeholders in `url`. A placeholder is only filled
   * in by the agent once it has an entry here.
   */
  path_parameters: z.array(customToolPathParameterSchema).optional(),
  /**
   * Mock configuration for simulations.
   * If not provided, a default simulation message will be used.
   */
  mock: customToolMockSchema.optional()
});
function checkCustomToolSettings(settings, ctx) {
  const urlIssue = findCustomToolUrlIssue(settings);
  if (urlIssue) {
    ctx.addIssue({
      code: "custom",
      message: urlIssue,
      path: ["settings", "url"]
    });
  }
}
function getCustomToolFilledPathPlaceholders(settings) {
  const pathParameters = settings.path_parameters;
  if (!pathParameters?.length) {
    return [];
  }
  return extractCustomToolPathPlaceholders(settings.url).reduce(
    (acc, placeholder) => {
      const pathParameter = pathParameters.find(
        ({ name }) => name === placeholder
      );
      if (pathParameter) {
        acc.push(pathParameter);
      }
      return acc;
    },
    []
  );
}
function findCustomToolUrlIssue(settings) {
  const { url, parameters, path_parameters: pathParameters } = settings;
  if (!URL.canParse(url)) {
    return "Enter a valid URL, including the scheme (for example https://api.example.com/orders).";
  }
  for (const segment of getCustomToolUrlPathSegments(url)) {
    if (segment.startsWith(":") && !pathPlaceholderNamePattern.test(segment.slice(1))) {
      return `"${segment}" is not a valid path placeholder. Use letters, numbers and underscores, for example :customer_id.`;
    }
  }
  for (const [key, value] of new URL(url).searchParams) {
    if (value.startsWith(":") && pathPlaceholderNamePattern.test(value.slice(1))) {
      return `Placeholders only work in the path, so ${value} is sent as-is for "${key}".`;
    }
  }
  const placeholders = extractCustomToolPathPlaceholders(url);
  for (const placeholder of placeholders) {
    if (!pathParameters?.some(({ name }) => name === placeholder)) {
      return `Describe what the agent should put in :${placeholder}.`;
    }
  }
  const seenNames = /* @__PURE__ */ new Set();
  for (const { name } of pathParameters ?? []) {
    if (!placeholders.includes(name)) {
      return `:${name} is not a placeholder in the URL.`;
    }
    if (seenNames.has(name)) {
      return `:${name} is described more than once.`;
    }
    seenNames.add(name);
  }
  for (const { name } of parameters ?? []) {
    if (placeholders.includes(name)) {
      return `"${name}" is both a path placeholder and a parameter. Rename one of them.`;
    }
  }
  return void 0;
}
const customToolPostPayloadSchema = baseToolPostPayloadSchema.extend({
  type: z.literal(CustomToolType),
  settings: customToolSettingsSchema
}).superRefine(
  (payload, ctx) => checkCustomToolSettings(payload.settings, ctx)
);
const customToolPatchPayloadSchema = baseToolPatchPayloadSchema.extend({
  type: z.literal(CustomToolType),
  settings: customToolSettingsSchema.optional()
}).superRefine((payload, ctx) => {
  if (payload.settings) {
    checkCustomToolSettings(payload.settings, ctx);
  }
});
export {
  CustomToolHttpMethod,
  CustomToolMockResponseType,
  CustomToolParameterType,
  CustomToolType,
  customToolHttpMethodSchema,
  customToolMockSchema,
  customToolParameterArrayItemTypeSchema,
  customToolParameterSchema,
  customToolParameterTypeSchema,
  customToolPatchPayloadSchema,
  customToolPathParameterSchema,
  customToolPostPayloadSchema,
  customToolSettingsSchema,
  defaultCustomToolTimeoutMs,
  extractCustomToolPathPlaceholders,
  findCustomToolUrlIssue,
  getCustomToolFilledPathPlaceholders
};
//# sourceMappingURL=custom.js.map
